import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope } from "@/lib/humhub-api/types";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

// Participation state constants
export const PARTICIPATION_STATE_NONE = 0;
export const PARTICIPATION_STATE_ACCEPTED = 3;

interface UseRespondToEventOptions {
  onSuccess?: (eventId: number, respondType: number) => void;
  onError?: (error: Error, eventId: number, respondType: number) => void;
}

interface UseRespondToEventReturn {
  isLoading: boolean;
  error: Error | null;
  respond: (
    eventId: number,
    respondType: number,
    scope?: RecurrenceScope,
  ) => Promise<void>;
}

export const useRespondToEvent = (
  options: UseRespondToEventOptions = {},
): UseRespondToEventReturn => {
  const { onSuccess, onError } = options;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      respondType,
      scope,
    }: {
      eventId: number;
      respondType: number;
      scope?: RecurrenceScope;
    }) => {
      return eventsApi.respondToEvent(
        eventId,
        respondType,
        (scope ?? "single") as RecurrenceScope,
      );
    },
    onSuccess: (_, variables) => {
      // クエリ無効化
      queryInvalidationHelpers.invalidateEventQueries(queryClient);
      if (variables.scope && variables.scope !== "single") {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "eventDetail",
        });
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "eventParticipants",
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["eventDetail", variables.eventId],
        });
        queryClient.invalidateQueries({
          queryKey: ["eventParticipants", variables.eventId],
        });
      }
      // スタディグループリスト関連のキャッシュも無効化
      queryClient.invalidateQueries({
        queryKey: studyGroupKeys.myStudyGroupsRoot,
      });

      // コールバック実行
      onSuccess?.(variables.eventId, variables.respondType);
    },
    onError: (error, variables) => {
      console.error("Error responding to event:", error);
      onError?.(error as Error, variables.eventId, variables.respondType);
    },
  });

  const respond = useCallback(
    async (
      eventId: number,
      respondType: number,
      scope?: RecurrenceScope,
    ): Promise<void> => {
      return mutation.mutateAsync({ eventId, respondType, scope });
    },
    [mutation],
  );

  return {
    isLoading: mutation.isPending,
    error: mutation.error,
    respond,
  };
};
